import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { Box, IconButton, Typography } from "@mui/material";
import React from "react";
import { usePagesTranslation } from "../../../../pages/locales";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import {
    playToolbarActionsGroupSx,
    playToolbarBarSx,
    playToolbarDividerSx,
    playToolbarPauseBtnSx,
    playToolbarSystemFont,
} from "../styles/playToolbarStyles";

const labelSx = {
    fontFamily: playToolbarSystemFont,
    fontSize: "0.625rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    display: "block",
    mb: 0.15,
    lineHeight: 1.2,
};

const valueSx = {
    fontFamily: playToolbarSystemFont,
    fontSize: "1.25rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
    lineHeight: 1.1,
};

/**
 * Play session bar: optional time on the left; Moves sits next to action chips on the right.
 *
 * `leading` puts extra content (landscape folds the back button and title in here) at the start of
 * the same row.
 */
export default function PlaySessionHud({
    timeLabel,
    moveCount,
    paused = false,
    manualPaused = false,
    onTogglePause = null,
    snapshotMarker = false,
    showTimer = false,
    actions = null,
    leading = null,
    /** "vertical" stacks the bar into a side rail (landscape). */
    orientation = "horizontal",
    modeLabel = null,
    /** Larger chips/type for 1080p content recording. */
    large = false,
}) {
    const { t } = usePagesTranslation();
    const { tokens } = useSlidingAppearance();
    const showPausedLabel = paused && manualPaused;
    const vertical = orientation === "vertical";
    const valueFontSx = large ? { ...valueSx, fontSize: "1.5rem" } : valueSx;
    const labelFontSx = large ? { ...labelSx, fontSize: "0.75rem" } : labelSx;

    const movesBlock = (
        <Box
            sx={{
                flex: "0 0 auto",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: vertical ? "center" : "right",
                pr: vertical ? 0 : 0.25,
            }}
        >
            <Typography component="span" sx={{ ...labelFontSx, color: tokens.textSubtle }}>
                {t("native.sliding.play.moves", { defaultValue: "Moves" })}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: vertical ? "center" : "flex-end",
                    minHeight: large ? 36 : 32,
                }}
            >
                <Typography component="span" sx={{ ...valueFontSx, color: tokens.text }}>
                    {moveCount}
                </Typography>
            </Box>
        </Box>
    );

    const modeBlock = modeLabel ? (
        <Box
            sx={{
                flex: "0 0 auto",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: vertical ? "center" : "right",
            }}
        >
            <Typography component="span" sx={{ ...labelFontSx, color: tokens.textSubtle }}>
                {t("native.sliding.play.mode", { defaultValue: "Mode" })}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: vertical ? "center" : "flex-end",
                    minHeight: large ? 36 : 32,
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        ...valueFontSx,
                        fontSize: large ? "1.05rem" : "0.95rem",
                        color: tokens.text,
                    }}
                >
                    {modeLabel}
                </Typography>
            </Box>
        </Box>
    ) : null;

    return (
        <Box
            sx={{
                ...playToolbarBarSx(tokens, { vertical, large }),
                ...(vertical
                    ? null
                    : { justifyContent: showTimer || leading ? "space-between" : "flex-end" }),
            }}
            aria-live="polite"
            {...(snapshotMarker
                ? { "aria-label": "PUZZLE_SNAPSHOT_PLAY_HUD_READY" }
                : {})}
        >
            {leading}

            {showTimer ? (
                <Box
                    sx={{
                        flex: "0 1 auto",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                    }}
                >
                    <Typography component="span" sx={{ ...labelFontSx, color: tokens.textSubtle }}>
                        {t("native.sliding.play.time", { defaultValue: "Time" })}
                        {showPausedLabel
                            ? ` · ${t("native.sliding.play.paused", {
                                  defaultValue: "Paused",
                              })}`
                            : ""}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.35,
                            minHeight: large ? 36 : 32,
                        }}
                    >
                        <Typography component="span" sx={{ ...valueFontSx, color: tokens.text }}>
                            {timeLabel}
                        </Typography>
                        {onTogglePause ? (
                            <IconButton
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onTogglePause();
                                }}
                                aria-label={
                                    manualPaused
                                        ? t("native.sliding.play.resume", {
                                              defaultValue: "Resume timer",
                                          })
                                        : t("native.sliding.play.pause", {
                                              defaultValue: "Pause timer",
                                          })
                                }
                                aria-pressed={manualPaused}
                                sx={playToolbarPauseBtnSx(tokens, { active: manualPaused })}
                            >
                                {manualPaused ? (
                                    <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />
                                ) : (
                                    <PauseRoundedIcon sx={{ fontSize: 18 }} />
                                )}
                            </IconButton>
                        ) : null}
                    </Box>
                </Box>
            ) : null}

            <Box
                sx={{
                    ...playToolbarActionsGroupSx({ vertical, large }),
                    ml: !vertical && (showTimer || leading) ? "auto" : 0,
                    gap: large ? 1.25 : 1,
                }}
            >
                {modeBlock}
                {movesBlock}
                {actions ? (
                    <>
                        <Box sx={playToolbarDividerSx(tokens, { vertical })} aria-hidden />
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: vertical ? "column" : "row",
                                alignItems: "center",
                                gap: 0.75,
                                flexShrink: 0,
                            }}
                        >
                            {actions}
                        </Box>
                    </>
                ) : null}
            </Box>
        </Box>
    );
}
